import { useApolloClient, useMutation } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import Search from "@mui/icons-material/Search";
import { Button, Typography, useTheme } from "@mui/material";
import { GridRowParams, GridSortModel } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { ExportDataButton } from "components/ExportDataButton";
import {
  WasteVendorDataCompletenessQuery,
  WasteVendorDataCompletenessQueryVariables,
} from "generated-graphql/graphql";
import { OmnisearchCommandButtonsContext, OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { useCallback, useMemo, useState } from "react";

import {
  DELETE_TENANT_WASTE_VENDOR,
  VENDOR_DATA_COMPLETENESS_QUERY,
  WASTE_VENDOR_OBSERVED_DATA_QUERY,
} from "./api";
import {
  ADDITIONAL_FILTER_KEYS,
  EXCLUDE_FILTER_COLUMNS,
  VendorDataRow,
  vendorDataColumns,
} from "./columns";
import { ExpectationDialog, ExpectationPrefill } from "./ExpectationDialog";
import { ObservedDataDrawer } from "./ObservedDataDrawer";

// Stable, module-level grid config (V2 requires stable references for its
// perf model).
function getItems(data: WasteVendorDataCompletenessQuery): VendorDataRow[] {
  return data.wasteVendorDataCompleteness.items;
}

function getCount(data: WasteVendorDataCompletenessQuery): number {
  return data.wasteVendorDataCompleteness.count;
}

function getRowId(row: VendorDataRow): string {
  return row.id;
}

// The grid's own filter keys ("tenantName", "vendorName") read more naturally
// as columns than the resolver's short "org"/"vendor" search keys, so alias
// them at the boundary rather than renaming either side.
const KEY_ALIASES = { tenantName: "org", vendorName: "vendor" };

// Passed to both the grid below and the persisted-settings reader, so the two
// can never derive different localStorage keys for the same grid.
const PERSISTENCE_KEY = "staff-waste-vendor-data-completeness";

// useGridPersistence (hooks/omnisearch-v2) derives this same key from
// persistenceKey and stores the grid's live sort model on it. Reading it here
// too — rather than adding a prop OmnisearchDataGridV2 doesn't expose — lets
// the export mirror whatever the grid is currently sorted by.
// use-local-storage-state (which both this hook and the grid's own
// persistence wrap) keeps every hook instance reading a key in sync within a
// tab, so this observes the grid's sort changes live, not just at mount.
const GRID_SETTINGS_STORAGE_KEY = `grid-settings-${PERSISTENCE_KEY}`;

// Deliberately loose: this reader only cares about sortModel and must never
// assume it owns the rest of the grid's persisted settings shape (pageSize,
// columnVisibilityModel, ...), which belongs to useGridPersistence.
interface PersistedVendorDataSort {
  sortModel?: GridSortModel;
}

const EMPTY_GRID_SETTINGS: PersistedVendorDataSort = {};

// Falls back to an empty sort — the server's own default order — when
// nothing has been persisted yet (a first-ever visit to the page).
const DEFAULT_SORT: GridSortModel = [];

// Drives the ExpectationDialog: `row` set is a true edit of an existing
// expectation; `row: null` with `prefill` locks the identity to an
// already-resolved (tenant, vendor) pair that has no expectation yet;
// `row: null` with no prefill is a from-scratch add.
interface DialogState {
  row: VendorDataRow | null;
  prefill?: ExpectationPrefill;
}

export const VendorData = () => {
  useBreadcrumb({ label: "Vendor Data" });
  const theme = useTheme();
  const alerts = useAlerts();
  const apolloClient = useApolloClient();

  const [gridSettings] = useLocalStorageState<PersistedVendorDataSort>(GRID_SETTINGS_STORAGE_KEY, {
    defaultValue: EMPTY_GRID_SETTINGS,
  });
  const persistedSortModel = gridSettings.sortModel ?? DEFAULT_SORT;

  const [dialogState, setDialogState] = useState<DialogState | null>(null);
  const [rowToDelete, setRowToDelete] = useState<VendorDataRow | null>(null);
  const [scanDrawerOpen, setScanDrawerOpen] = useState(false);

  const [deleteExpectation, { loading: deleting }] = useMutation(DELETE_TENANT_WASTE_VENDOR, {
    refetchQueries: [VENDOR_DATA_COMPLETENESS_QUERY],
  });

  const handleAddExpectation = useCallback(() => {
    setDialogState({ row: null });
  }, []);

  const handleEditRow = useCallback((row: VendorDataRow) => {
    if (row.isExpected) {
      setDialogState({ row });
      return;
    }

    if (row.vendorId) {
      // Already resolved to a canonical vendor, just never adopted — lock
      // the identity and let staff fill in the schedule.
      setDialogState({
        row: null,
        prefill: {
          tenantId: row.tenantId,
          wasteManifestVendorId: row.vendorId,
          tenantName: row.tenantName,
          vendorName: row.vendorName ?? undefined,
        },
      });
      return;
    }

    // No canonical vendor matched yet — nothing to lock the identity to, so
    // open a blank dialog and let staff pick both fields directly.
    setDialogState({ row: null });
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogState(null);
  }, []);

  // The scan drawer's onAdopt hands back only the pair's identity ids (see
  // ObservedDataDrawer's own contract/test) — it doesn't know how the grid
  // wants to display the pair. The drawer's own `wasteVendorObservedData`
  // fetch already has the display names on hand and, having just been
  // fetched to render the row staff clicked Adopt on, is still sitting in
  // the Apollo cache — read it back here instead of a second network round
  // trip, so the dialog can show real names instead of raw ids.
  const handleAdoptObservedPair = useCallback(
    (adopted: { tenantId: string; wasteManifestVendorId: string }) => {
      const cached = apolloClient.readQuery({ query: WASTE_VENDOR_OBSERVED_DATA_QUERY });
      const matchingPair = cached?.wasteVendorObservedData.find(
        (pair) =>
          pair.tenantId === adopted.tenantId && pair.vendorId === adopted.wasteManifestVendorId
      );

      setScanDrawerOpen(false);
      setDialogState({
        row: null,
        prefill: {
          tenantId: adopted.tenantId,
          wasteManifestVendorId: adopted.wasteManifestVendorId,
          tenantName: matchingPair?.tenantName,
          vendorName: matchingPair?.vendorName ?? undefined,
        },
      });
    },
    [apolloClient]
  );

  const handleDeleteRow = useCallback((row: VendorDataRow) => {
    setRowToDelete(row);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!rowToDelete) return;
    try {
      await deleteExpectation({ variables: { id: rowToDelete.id } });
      alerts.success(`Deleted the expectation for "${rowToDelete.vendorName ?? "this vendor"}".`);
      setRowToDelete(null);
    } catch (err) {
      alerts.error(
        `Failed to delete the expectation: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  }, [rowToDelete, deleteExpectation, alerts]);

  const handleRowClick = useCallback(
    ({ row }: GridRowParams<VendorDataRow>) => {
      handleEditRow(row);
    },
    [handleEditRow]
  );

  const columns = useMemo(
    () => vendorDataColumns({ onEdit: handleEditRow, onDelete: handleDeleteRow }),
    [handleEditRow, handleDeleteRow]
  );

  const buildVariables = useCallback(
    (
      search: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): WasteVendorDataCompletenessQueryVariables => ({
      search,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
    }),
    []
  );

  const commandButtons = useCallback(
    ({ search }: OmnisearchCommandButtonsContext) => [
      <Button
        key="add-expectation"
        variant="contained"
        startIcon={<Add />}
        onClick={handleAddExpectation}
      >
        Add expectation
      </Button>,
      <Button
        key="scan-observed-data"
        variant="outlined"
        startIcon={<Search />}
        onClick={() => setScanDrawerOpen(true)}
      >
        Scan observed vendor data
      </Button>,
      <ExportDataButton
        key="export"
        exportType="wasteVendorDataCompletenessForStaff"
        fileName="waste-vendor-data-completeness"
        filter={{ search, sort: persistedSortModel }}
      />,
    ],
    [persistedSortModel, handleAddExpectation]
  );

  return (
    <>
      <OmnisearchDataGridV2<
        WasteVendorDataCompletenessQuery,
        WasteVendorDataCompletenessQueryVariables,
        VendorDataRow
      >
        query={VENDOR_DATA_COMPLETENESS_QUERY}
        persistenceKey={PERSISTENCE_KEY}
        columns={columns}
        getItems={getItems}
        getCount={getCount}
        getRowId={getRowId}
        buildVariables={buildVariables}
        keyAliases={KEY_ALIASES}
        additionalFilterKeys={ADDITIONAL_FILTER_KEYS}
        excludeFilterColumns={EXCLUDE_FILTER_COLUMNS}
        // isActive defaults to false on a new expectation, so an active-only
        // default filter would render an empty grid on day one — leave the
        // omnisearch filter unset instead.
        initialPageSize={25}
        commandButtons={commandButtons}
        // Clicking anywhere on a row opens the same editor the Edit action does.
        // The action buttons stop propagation so they don't also trigger this.
        onRowClick={handleRowClick}
        noRowsMessage="No waste vendor data completeness rows found."
      />

      {dialogState && (
        <ExpectationDialog
          open
          onClose={handleDialogClose}
          row={dialogState.row}
          prefill={dialogState.prefill}
        />
      )}

      <ObservedDataDrawer
        open={scanDrawerOpen}
        onClose={() => setScanDrawerOpen(false)}
        onAdopt={handleAdoptObservedPair}
      />

      <ConfirmDialog
        open={!!rowToDelete}
        onClose={() => setRowToDelete(null)}
        header={`Delete the expectation for "${rowToDelete?.vendorName ?? "this vendor"}"?`}
        confirmText="Yes, delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        info={
          <Typography variant="body2" mb={theme.spacing(2)}>
            This organization will no longer be tracked against a delivery schedule for this vendor.
            Any historical data already received is unaffected.
          </Typography>
        }
        loading={deleting}
        disableConfirm={!rowToDelete}
      />
    </>
  );
};
